/**
 * CMT (Classification Management Tool) shared types.
 *
 * The CmtResult is the value returned by both the mock CMT component (dev)
 * and the real CMT popup (production). The wrapper normalises the real tool's
 * XML output into this shape before calling onChange.
 */

export type CmtResult = {
  /** IC classification level. Currently always "U". */
  classification: string
  /** Dissemination controls. Empty array = no controls. */
  disseminationControls: string[]
}

/**
 * The three valid combinations in display / banner string form.
 * Used by the mock to drive the radio group.
 */
export type CmtOption = {
  label: string
  result: CmtResult
}

export const CMT_OPTIONS: CmtOption[] = [
  {
    label: 'UNCLASSIFIED',
    result: { classification: 'U', disseminationControls: [] },
  },
  {
    label: 'UNCLASSIFIED//FOUO',
    result: { classification: 'U', disseminationControls: ['FOUO'] },
  },
  {
    label: 'UNCLASSIFIED//FOUO//NOFORN',
    result: { classification: 'U', disseminationControls: ['FOUO', 'NF'] },
  },
]

/** Render a CmtResult as a banner string, e.g. "UNCLASSIFIED//FOUO". */
export function formatCmtResult(result: CmtResult | undefined | null): string {
  if (!result) return 'UNCLASSIFIED'
  const parts = ['UNCLASSIFIED', ...result.disseminationControls.map(d => d === 'NF' ? 'NOFORN' : d)]
  return parts.join('//')
}

/** Find the matching CmtOption for a given result (by disseminationControls). */
export function matchCmtOption(result: CmtResult | undefined | null): CmtOption {
  if (!result) return CMT_OPTIONS[0]
  const dc = [...result.disseminationControls].sort().join(',')
  return CMT_OPTIONS.find(o => [...o.result.disseminationControls].sort().join(',') === dc) ?? CMT_OPTIONS[0]
}
